"use client";

import React, { useState, useMemo } from "react";

import { ApplicationStatusEnum } from "graphql/type.ts";

import { TabItem, TabItems } from "./TabItems";

export type Tab = TabItem & {
  content: React.ReactNode;
};

interface TabBarProps {
  tabs: Tab[];
  initialTab?: string;
  tabControlClassName?: string;
  applicationStatus?: ApplicationStatusEnum; // アプリケーションのステータスを示す数値
  onTabChange?: (id: string) => Promise<boolean>; // タブが変更されたときの非同期コールバック
}

export const TabBar: React.FC<TabBarProps> = ({ tabs, initialTab, tabControlClassName, onTabChange }) => {
  const [activeTab, setActiveTab] = useState(initialTab || tabs[0].id);

  const handleTabClick = async (id: string) => {
    // タブがアクティブの時は何もしない
    if (activeTab === id) return;

    if (onTabChange) {
      const shouldProceed = await onTabChange(id);
      if (shouldProceed) {
        setActiveTab(id); // 確認後に状態を更新
      }
    } else {
      setActiveTab(id); // onTabChangeがない場合は即時更新
    }
  };

  const memoizedTabs = useMemo(() => tabs.map(({ id, label, className }) => ({ id, label, className })), [tabs]);

  return (
    <div role="tablist">
      <TabItems
        tabs={memoizedTabs}
        activeTab={activeTab}
        wrapperClassName={tabControlClassName}
        onTabClick={handleTabClick}
      />
      {tabs.map(({ id, content }) => (
        <div
          role="tabpanel"
          id={`panel-${id}`}
          key={id}
          hidden={activeTab !== id}
          className={activeTab !== id ? "hidden" : "mt-1"}
          tabIndex={0}
          aria-labelledby={`tab-${id}`}
        >
          {content}
        </div>
      ))}
    </div>
  );
};
